/* utility */
export const THAI_DIGIT = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙'];
/*--------------------------------------------------------------------------------*/
export const thaiDigit = (input: number | string): string => {
    return input.toString()
        .replace(/\d/g, (digit: string) => THAI_DIGIT[parseInt(digit)]);
};
/*--------------------------------------------------------------------------------*/
export const cidCheck = (cid: string): boolean => {
    if (!Number(cid)) return false;
    if (cid.substring(0, 1) == '0') return false;
    if (cid.length !== 13) return false;
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        sum += parseFloat(cid.charAt(i)) * (13 - i);
    }
    return (11 - sum % 11) % 10 === parseFloat(cid.charAt(12));
};
/*--------------------------------------------------------------------------------*/
export const openWinPopup = (
    url: string,
    name: string,
    width: number = 800,
    height: number = 600,
): Window | null => {
    const dualScreenLeft = window.screenLeft ?? window.screenX;
    const dualScreenTop = window.screenTop ?? window.screenY;
    const screenWidth = window.innerWidth
        ?? document.documentElement.clientWidth
        ?? screen.width;
    const screenHeight = window.innerHeight
        ?? document.documentElement.clientHeight
        ?? screen.height;
    const left = (screenWidth / 2) - (width / 2) + dualScreenLeft;
    const top = (screenHeight / 2) - (height / 2) + dualScreenTop;
    const options = [
        `width=${width}`,
        `height=${height}`,
        `top=${top}`,
        `left=${left}`,
        'scrollbars=yes',
        'resizable=yes',
        'toolbar=no',
        'location=no',
    ].join(',');
    const newWindow = window.open(url, name, options);
    if (newWindow) newWindow.focus();
    return newWindow;
};
/*--------------------------------------------------------------------------------*/
export const strEscape = (str: string): string => {
    // eslint-disable-next-line no-control-regex
    return String(str).replace(/[\0\x08\x09\x1a\n\r"'\\%]/g, (char) => {
        switch (char) {
            case "\0": return "\\0";
            case "\x08": return "\\b";
            case "\x09": return "\\t";
            case "\x1a": return "\\z";
            case "\n": return "\\n";
            case "\r": return "\\r";
            case "\"": return "\\\"";
            case "'": return "\\'";
            case "\\": return "\\\\";
            case "%": return "\\%";
            default: return char;
        }
    }).trim();
};
/*--------------------------------------------------------------------------------*/
export type NumFormatOptions = {
    minDecimals?: number;
    maxDecimals?: number;
    placeholder?: string;
    showComma?: boolean;
};
export const numFormat = (
    value: number | string,
    opts: NumFormatOptions = {}
): string => {
    const {
        minDecimals = 0,
        maxDecimals = 2,
        placeholder = '-',
        showComma = true
    } = opts;
    const n = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(n)) return placeholder;
    if (n === 0) return placeholder;
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
        useGrouping: showComma,
    }).format(n);
};
/*--------------------------------------------------------------------------------*/
export const numCompact = (num: number, fixed: number = 0): string => {
    return new Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: fixed
    }).format(num);
};
/*--------------------------------------------------------------------------------*/
export const strFormat = (
    value: string | number,
    pattern: string,
    placeholder: string = '#'
): string => {
    const v = String(value);
    let i = 0;
    const formatted = pattern.replace(/#/g, () => v[i++] ?? placeholder);
    const remaining = v.slice(i);
    return formatted + (remaining ? remaining : '');
};
/*--------------------------------------------------------------------------------*/
export const base64Encode = (str: string): string => {
    const bytes = new TextEncoder().encode(str);
    const binString = String.fromCodePoint(...bytes);
    return btoa(binString);
};
export const base64Decode = (base64: string): string => {
    const binString = atob(base64);
    const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0)!);
    return new TextDecoder().decode(bytes);
};
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (err) => reject(err);
    });
};
/*--------------------------------------------------------------------------------*/
export type JwtClaims = {
    iss: string;
    aud: string;
    iat: number;
    exp: number;
    sub: number;
};
export const jwtParse = (token: string): JwtClaims | null => {
    try { return JSON.parse(atob(token.split('.')[1])); }
    catch { return null; }
};
/*--------------------------------------------------------------------------------*/
